import React from "react";

// API functions for personal information management
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Add new personal information
 * @param {Object} personalInfoData - Personal information data
 * @returns {Promise<Object>} - API response
 */
export const addPersonalInformation = async (personalInfoData) => {
  try {
    const response = await fetch(`${API_URL}/api/PersonalInformation/AddPersonalInformation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(personalInfoData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Update existing personal information
 * @param {Object} personalInfoData - Personal information data with Id
 * @returns {Promise<Object>} - API response
 */
export const updatePersonalInformation = async (personalInfoData) => {
  try {
    const response = await fetch(`${API_URL}/api/PersonalInformation/UpdatePersonalInformation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(personalInfoData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Delete personal information
 * @param {Object} personalInfoData - Object containing Id
 * @returns {Promise<Object>} - API response
 */
export const deletePersonalInformation = async (personalInfoData) => {
  try {
    const response = await fetch(`${API_URL}/api/PersonalInformation/DeletePersonalInformation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(personalInfoData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Get all personal information for a specific building
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object>} - API response with personal information array
 */
export const getPersonalInformation = async (buildingId) => {
  try {
    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const response = await fetch(`${API_URL}/api/PersonalInformation/GetPersonalInformation/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Get single personal information item by ID
 * @param {number} id - Personal Information ID
 * @returns {Promise<Object>} - API response with personal information item
 */
export const getPersonalInformationItem = async (id) => {
  try {
    if (!id) {
      throw new Error('Personal Information ID is required');
    }

    const response = await fetch(`${API_URL}/api/PersonalInformation/GetPersonalInformationItem/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};